import { ref, watch } from 'vue'
import type { Ref } from 'vue'

interface Options {
  signal: Ref<boolean>;
  delay: number;
  deactivationDelay: number;
}

const stateMap = {
  ready: () => () => {},
  pending: () => () => {},
  active: () => () => {},
}

type StateId = keyof typeof stateMap

export const usePreloaderFSM = ({ signal, delay, deactivationDelay }: Options) => {
  const isPreloaderVisible = ref(false)
  let stopState = () => {}

  const setState = (stateId: StateId) => {
    stopState()
    stopState = stateMap[stateId]()
  }

  stateMap.ready = () => {
    isPreloaderVisible.value = false
  
    return watch(signal, (isAwaitProcessEnd) => {
      if (isAwaitProcessEnd) {
        setState('pending')
      }
    })
  }
  
  stateMap.pending = () => {
    let processPromiseReject = () => {}

    new Promise<void>((resolve, reject) => {
      processPromiseReject = reject
      setTimeout(() => {
        resolve()
      }, delay)
    })
      .then(() => {
        setState('active')
      })
      .catch(() => {
        setState('ready')
      })

    return watch(signal, (isAwaitProcessEnd) => {
      if (!isAwaitProcessEnd) {
        processPromiseReject()
      }
    })
  }
  
  stateMap.active = () => {
    isPreloaderVisible.value = true
    
    let processPromiseResolve = () => {}
    const asyncProcessPromise = new Promise<void>((resolve, _) => {
      processPromiseResolve = resolve
    })

    Promise.allSettled([
      asyncProcessPromise,
      new Promise<void>((resolve, _) => {
        setTimeout(() => {
          resolve()
        }, deactivationDelay)
      })
    ])
      .then(() => {
        setState('ready')
      })
    
    return watch(signal, (isAwaitProcessEnd) => {
      if (!isAwaitProcessEnd) {
        processPromiseResolve()
      }
    })
  }

  setState(signal.value ? 'pending' : 'ready')

  return { isPreloaderVisible }
}
